import { createFileRoute, Link } from '@tanstack/react-router'
import { DocsLayout } from '~/components/DocsLayout'
import { useAppStore } from '~/lib/store'
import type { DocNode } from '~/lib/api'

export const Route = createFileRoute('/docs/')({
    component: DocsPage,
})

function DocsPage() {
    const { docTree } = useAppStore()

    return (
        <DocsLayout>
            <div className="p-6 max-w-4xl animate-fade-in">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">
                        {docTree?.title || 'Documentation'}
                    </h1>
                    <p className="text-slate-400 text-lg">
                        {docTree?.description || 'Browse all project documentation organized by category.'}
                    </p>
                </header>

                {!docTree ? (
                    <div className="text-slate-400">Loading...</div>
                ) : (
                    <div className="space-y-8">
                        {docTree.sections.map((section) => (
                            <section key={section.id} className="animate-slide-up">
                                {/* Section Header */}
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-2xl">{section.icon}</span>
                                    <div>
                                        <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                                        {section.description && (
                                            <p className="text-sm text-slate-400">{section.description}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Section Content */}
                                <div className="grid gap-4 md:grid-cols-2">
                                    {section.folders.map((node) => (
                                        node.type === 'dir' ? (
                                            <FolderCard key={node.path} folder={node} />
                                        ) : (
                                            <DocCard key={node.path} doc={node} />
                                        )
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </DocsLayout>
    )
}

function FolderCard({ folder }: { folder: DocNode }) {
    const docCount = folder.children?.filter(c => c.name.endsWith('.md')).length || 0
    const firstDoc = folder.children?.find(c => c.name.endsWith('.md'))

    return (
        <div className={`bg-slate-800/50 rounded-xl border overflow-hidden transition-all hover:border-slate-500 ${folder.primary
            ? 'border-blue-500/50 ring-1 ring-blue-500/20'
            : 'border-slate-700'
            }`}>
            {/* Card Header */}
            <div className={`px-4 py-3 border-b ${folder.primary
                ? 'bg-blue-600/10 border-blue-500/30'
                : 'bg-slate-800 border-slate-700'
                }`}>
                <div className="flex items-center gap-3">
                    <span className="text-xl">{folder.icon || '📁'}</span>
                    <div className="flex-1">
                        <h3 className={`font-semibold ${folder.primary ? 'text-blue-400' : 'text-white'}`}>
                            {folder.title || folder.name}
                        </h3>
                        {folder.description && (
                            <p className="text-xs text-slate-400 mt-0.5">{folder.description}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Card Content - Doc Links */}
            <div className="divide-y divide-slate-700/50">
                {folder.children?.slice(0, 4).map((doc) => (
                    <Link
                        key={doc.path}
                        to="/docs/$"
                        params={{ _splat: doc.path.replace('.md', '') }}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-700/30 transition-colors"
                    >
                        <span className="text-slate-400 text-sm">📄</span>
                        <span className="text-sm text-slate-200">{doc.title || doc.name.replace('.md', '')}</span>
                    </Link>
                ))}

                {docCount > 4 && (
                    <Link
                        to="/docs/$"
                        params={{ _splat: firstDoc ? firstDoc.path.replace('.md', '') : '' }}
                        className="flex items-center justify-center gap-2 px-4 py-2 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700/30 transition-colors"
                    >
                        <span>View all {docCount} documents</span>
                        <span>→</span>
                    </Link>
                )}
            </div>
        </div>
    )
}

function DocCard({ doc }: { doc: DocNode }) {
    return (
        <Link
            to="/docs/$"
            params={{ _splat: doc.path.replace('.md', '') }}
            className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 hover:border-slate-500 transition-all group"
        >
            <div className="flex items-start gap-3">
                <span className="text-2xl mt-1">📄</span>
                <div className="flex-1">
                    <h3 className="font-semibold text-white group-hover:text-blue-400 transition-colors">
                        {doc.title || doc.name.replace('.md', '')}
                    </h3>
                    {doc.description && (
                        <p className="text-sm text-slate-400 mt-1">{doc.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-3 text-xs text-blue-400 font-medium">
                        Read Document <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                </div>
            </div>
        </Link>
    )
}
